<template>
  <div class="mt-3">
    <!-- Summary cards -->
    <div class="row mb-3">
      <div class="col-md-3 mb-3" v-for="stat in summaryStats" :key="stat.label">
        <div class="hc-card">
          <div class="card-body py-3">
            <small class="text-muted text-uppercase" style="font-size:.65rem;letter-spacing:.05em;">
              {{ stat.label }}
            </small>
            <div v-if="loading" class="mt-2">
              <div class="spinner-border spinner-border-sm text-secondary"></div>
            </div>
            <h5 v-else class="mb-0 mt-1 font-weight-bold" :class="stat.color">
              {{ stat.value }}
            </h5>
          </div>
        </div>
      </div>
    </div>

    <!-- Revenue chart -->
    <div class="hc-card mb-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0 font-weight-bold">Monthly Subscription Revenue</h6>
          <div>
            <button
              v-for="m in [3, 6, 12]"
              :key="m"
              class="btn mr-1"
              :class="selectedMonths === m ? 'btn-primary' : 'btn-outline-secondary'"
              @click="selectedMonths = m; fetchRevenue()"
            >{{ m }}m</button>
          </div>
        </div>
        <div v-if="chartLoading" class="text-center py-4">
          <div class="spinner-border text-secondary" role="status"></div>
        </div>
        <div v-else-if="revenueChartData" style="position:relative;height:200px;">
          <line-chart :data="revenueChartData" :options="chartOptions" />
        </div>
      </div>
    </div>

    <!-- Revenue by merchant -->
    <div class="hc-card mb-3">
      <div class="card-body p-0">
        <div class="d-flex justify-content-between align-items-center p-3">
          <h6 class="mb-0 font-weight-bold">Revenue by Merchant</h6>
          <select v-model="merchantDays" @change="fetchMerchantRevenue(1)" class="pill-select" style="width:130px;">
            <option :value="7">Last 7 days</option>
            <option :value="30">Last 30 days</option>
            <option :value="90">Last 90 days</option>
          </select>
        </div>
        <div v-if="merchantLoading" class="text-center py-4">
          <div class="spinner-border text-secondary" role="status"></div>
        </div>
        <table v-else class="table table-hover mb-0">
          <thead class="thead-light">
            <tr>
              <th class="pl-3">#</th>
              <th>Merchant</th>
              <th>Orders</th>
              <th>Revenue (KES)</th>
              <th>AOV (KES)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in merchantRevenue" :key="item.company_id">
              <td class="pl-3 text-muted" style="font-size:.8rem;">
                {{ ((merchantMeta.current_page - 1) * 20) + i + 1 }}
              </td>
              <td class="font-weight-bold" style="font-size:.85rem;">{{ item.name }}</td>
              <td><small>{{ item.order_count.toLocaleString() }}</small></td>
              <td><small class="font-weight-bold">{{ item.revenue.toLocaleString() }}</small></td>
              <td><small class="text-muted">{{ item.aov.toLocaleString() }}</small></td>
            </tr>
            <tr v-if="merchantRevenue.length === 0">
              <td colspan="5" class="text-center text-muted py-3">No revenue data</td>
            </tr>
          </tbody>
        </table>
        <div v-if="merchantMeta.last_page > 1" class="d-flex justify-content-between align-items-center px-3 py-2 border-top">
          <small class="text-muted">{{ merchantMeta.total }} merchants · Page {{ merchantMeta.current_page }} of {{ merchantMeta.last_page }}</small>
          <div>
            <button class="btn btn-outline-secondary mr-1" :disabled="merchantMeta.current_page <= 1" @click="fetchMerchantRevenue(merchantMeta.current_page - 1)">
              <i class="bi bi-chevron-left"></i>
            </button>
            <button class="btn btn-outline-secondary" :disabled="merchantMeta.current_page >= merchantMeta.last_page" @click="fetchMerchantRevenue(merchantMeta.current_page + 1)">
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Subscriptions table -->
    <div class="hc-card">
      <div class="card-body p-0">
        <div class="d-flex justify-content-between align-items-center p-3">
          <h6 class="mb-0 font-weight-bold">Active Subscriptions</h6>
          <div class="d-flex align-items-center">
            <select v-model="tableFilters.plan" @change="fetchSubscriptions(1)" class="pill-select mr-2" style="width:150px;">
              <option value="">All plans</option>
              <option v-for="plan in plans" :key="plan.id" :value="plan.id">{{ plan.name }}</option>
            </select>
            <select v-model="tableFilters.sort" @change="fetchSubscriptions(1)" class="pill-select" style="width:160px;">
              <option value="due_asc">Due: Soonest first</option>
              <option value="due_desc">Due: Latest first</option>
              <option value="amount_desc">Amount: High to low</option>
            </select>
          </div>
        </div>

        <div v-if="tableLoading" class="text-center py-5">
          <div class="spinner-border text-secondary" role="status"></div>
        </div>

        <table v-else class="table table-hover mb-0">
          <thead class="thead-light">
            <tr>
              <th class="pl-3">Company</th>
              <th>Plan</th>
              <th>Amount (KES)</th>
              <th>Due Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in subscriptions" :key="item.id">
              <td class="pl-3">
                <span class="font-weight-bold" style="font-size:.85rem;">{{ item.company ? item.company.name : '—' }}</span>
              </td>
              <td>
                <small>{{ item.subscription_plan ? item.subscription_plan.name : '—' }}</small>
              </td>
              <td>
                <small class="font-weight-bold">
                  {{ item.subscription_plan ? Number(item.subscription_plan.amount).toLocaleString() : '—' }}
                </small>
              </td>
              <td>
                <small :class="isDuePast(item.due_date) ? 'text-danger' : 'text-muted'">
                  {{ formatDate(item.due_date) }}
                </small>
              </td>
              <td>
                <span :class="['badge', isDuePast(item.due_date) ? 'badge-danger' : 'badge-primary']">
                  {{ isDuePast(item.due_date) ? 'Overdue' : 'Active' }}
                </span>
              </td>
            </tr>
            <tr v-if="subscriptions.length === 0">
              <td colspan="5" class="text-center text-muted py-3">No subscriptions found</td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div v-if="tableMeta.last_page > 1" class="d-flex justify-content-between align-items-center px-3 py-2 border-top">
          <small class="text-muted">{{ tableMeta.total }} total · Page {{ tableMeta.current_page }} of {{ tableMeta.last_page }}</small>
          <div>
            <button class="btn btn-outline-secondary mr-1" :disabled="tableMeta.current_page <= 1" @click="fetchSubscriptions(tableMeta.current_page - 1)">
              <i class="bi bi-chevron-left"></i>
            </button>
            <button class="btn btn-outline-secondary" :disabled="tableMeta.current_page >= tableMeta.last_page" @click="fetchSubscriptions(tableMeta.current_page + 1)">
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Line as LineChart } from 'vue-chartjs';
import {
  Chart as ChartJS, Title, Tooltip, Legend,
  CategoryScale, LinearScale, PointElement, LineElement, Filler,
} from 'chart.js';

ChartJS.register(Title, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Filler);

export default {
  name: 'AdminRevenueTab',
  components: { LineChart },

  data() {
    return {
      loading: false,
      chartLoading: false,
      tableLoading: false,
      selectedMonths: 6,
      revenueRaw: null,
      subscriptions: [],
      plans: [],
      tableMeta: {},
      summaryData: {},
      tableFilters: { plan: '', sort: 'due_asc' },
      merchantLoading: false,
      merchantRevenue: [],
      merchantMeta: {},
      merchantDays: 30,
    };
  },

  computed: {
    summaryStats() {
      return [
        {
          label: 'MRR',
          value: 'KES ' + Number(this.summaryData.mrr || 0).toLocaleString(),
          color: 'text-primary',
        },
        {
          label: 'Active Subscriptions',
          value: this.summaryData.active_count || 0,
          color: 'text-primary',
        },
        {
          label: 'Overdue',
          value: this.summaryData.overdue_count || 0,
          color: 'text-danger',
        },
        {
          label: 'Trial',
          value: this.summaryData.trial_count || 0,
          color: 'text-dark',
        },
      ];
    },

    revenueChartData() {
      if (!this.revenueRaw) return null;
      return {
        labels: this.revenueRaw.labels,
        datasets: [{
          label: 'KES',
          data: this.revenueRaw.data,
          borderColor: '#4e73df',
          backgroundColor: 'rgba(78,115,223,0.1)',
          tension: 0.4,
          fill: true,
          pointRadius: 3,
        }],
      };
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { callback: v => 'KES ' + Number(v).toLocaleString() },
          },
        },
      };
    },
  },

  mounted() {
    this.fetchRevenue();
    this.fetchSubscriptions(1);
    this.fetchSummary();
    this.fetchPlans();
    this.fetchMerchantRevenue(1);
  },

  methods: {
    async fetchRevenue() {
      this.chartLoading = true;
      try {
        const { data } = await axios.get('/api/admin/charts/revenue-trend', {
          params: { months: this.selectedMonths },
        });
        this.revenueRaw = data;
      } finally {
        this.chartLoading = false;
      }
    },

    async fetchSummary() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/admin/dashboard');
        this.summaryData = {
          mrr: data.kpis.mrr,
          active_count: data.kpis.active_companies,
          overdue_count: data.kpis.overdue_companies,
          trial_count: data.kpis.trial_companies,
        };
      } finally {
        this.loading = false;
      }
    },

    async fetchPlans() {
      try {
        const { data } = await axios.get('/api/all-subscription-plan');
        this.plans = data;
      } catch (e) { /* silent */ }
    },

    async fetchSubscriptions(page = 1) {
      this.tableLoading = true;
      const sortMap = {
        due_asc: { sort: 'due_date', direction: 'asc' },
        due_desc: { sort: 'due_date', direction: 'desc' },
        amount_desc: { sort: 'subscription_plan_id', direction: 'desc' },
      };
      const sortParams = sortMap[this.tableFilters.sort] || sortMap.due_asc;

      try {
        const { data } = await axios.get('/api/admin/companies', {
          params: {
            page,
            per_page: 30,
            status: 'active',
            plan_id: this.tableFilters.plan || undefined,
            sort: sortParams.sort,
            direction: sortParams.direction,
          },
        });
        // Extract billing info from company objects
        this.subscriptions = data.data.map(c => ({
          id: c.id,
          company: { name: c.name },
          due_date: c.billing ? c.billing.due_date : null,
          subscription_plan: c.billing ? c.billing.subscription_plan : null,
        }));
        this.tableMeta = {
          current_page: data.current_page,
          last_page: data.last_page,
          total: data.total,
        };
      } finally {
        this.tableLoading = false;
      }
    },

    async fetchMerchantRevenue(page = 1) {
      this.merchantLoading = true;
      try {
        const { data } = await axios.get('/api/admin/revenue/by-merchant', {
          params: { days: this.merchantDays, per_page: 20, page },
        });
        this.merchantRevenue = data.data;
        this.merchantMeta    = { current_page: data.current_page, last_page: data.last_page, total: data.total };
      } finally {
        this.merchantLoading = false;
      }
    },

    isDuePast(date) {
      return date && moment(date).isBefore(moment());
    },

    formatDate(date) {
      return date ? moment(date).format('D MMM YY') : '—';
    },
  },
};
</script>

<style scoped>
.hc-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 14px;
}
.pill-select {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 10px;
  background: #fff;
  font-size: 13px;
  color: #0f172a;
}
</style>
